import type { TFunction } from 'i18next';

import type { IconVariant } from '../components/Icon';
import type {
  FilterWidth,
  GroupOptions,
} from '../components/Filter/filter-types';

export interface PoiFilterDefinition {
  key: string;
  icon: IconVariant;
  titleKey: string;
  subtitleKey?: string;
  filterType: 'multiChoice' | 'multiChoiceToggle' | 'select' | 'range';
  width?: FilterWidth;
  clearValue?: string;
}

export const poiFilterRegistry = {
  design: {
    key: 'design',
    icon: 'StudyDesignCheck',
    titleKey: 'poiExperience.filters.titles.studyDesign',
    filterType: 'multiChoiceToggle',
    width: 'full',
    clearValue: 'SeeAllDesigns',
  },
  status: {
    key: 'status',
    icon: 'Check',
    titleKey: 'poiExperience.filters.titles.status',
    filterType: 'multiChoice',
  },
  smokersNeverEligible: {
    key: 'smokersNeverEligible',
    icon: 'FactCheck',
    titleKey: 'poiExperience.filters.titles.neverSmokers',
    subtitleKey: 'poiExperience.filters.subtitles.neverSmokers',
    filterType: 'multiChoice',
  },
  smokingCessationOffered: {
    key: 'smokingCessationOffered',
    icon: 'SmokeFree',
    titleKey: 'poiExperience.filters.titles.smokingCessation',
    subtitleKey: 'poiExperience.filters.subtitles.smokingCessation',
    filterType: 'multiChoice',
  },
  sex: {
    key: 'sex',
    icon: 'Person',
    titleKey: 'poiExperience.filters.titles.sex',
    filterType: 'multiChoice',
  },
  targetOutreach: {
    key: 'targetOutreach',
    icon: 'Target',
    titleKey: 'poiExperience.filters.titles.methods',
    subtitleKey: 'poiExperience.filters.subtitles.methods',
    filterType: 'multiChoice',
  },
  useCade: {
    key: 'useCade',
    icon: 'Computer',
    titleKey: 'poiExperience.filters.titles.tools',
    subtitleKey: 'poiExperience.filters.subtitles.tools',
    filterType: 'multiChoice',
  },
  riskModellingUsed: {
    key: 'riskModellingUsed',
    icon: 'Calculator',
    titleKey: 'poiExperience.filters.titles.riskModelling',
    subtitleKey: 'poiExperience.filters.subtitles.riskModelling',
    filterType: 'multiChoice',
  },
  biomarkersCollected: {
    key: 'biomarkersCollected',
    icon: 'FingerPrint',
    titleKey: 'poiExperience.filters.titles.biomarkers',
    subtitleKey: 'poiExperience.filters.subtitles.biomarkers',
    filterType: 'multiChoice',
  },
  resultsAvailable: {
    key: 'resultsAvailable',
    icon: 'Find',
    titleKey: 'poiExperience.filters.titles.results',
    subtitleKey: 'poiExperience.filters.subtitles.results',
    filterType: 'multiChoice',
  },
  dateRange: {
    key: 'dateRange',
    icon: 'DateRange',
    titleKey: 'poiExperience.filters.titles.dateRange',
    filterType: 'range',
  },
  region: {
    key: 'region',
    icon: 'Globe',
    titleKey: 'poiExperience.filters.titles.region',
    filterType: 'select',
  },
} as const satisfies Record<string, PoiFilterDefinition>;

export type PoiFilterKey = keyof typeof poiFilterRegistry;

// Option builders

export const buildYesNoOptions = (t: TFunction): GroupOptions[] => [
  { label: t('poiExperience.filters.options.seeAll'), value: undefined },
  { value: 'Yes', label: t('poiExperience.filters.options.yes') },
  { value: 'No', label: t('poiExperience.filters.options.no') },
];
